import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { Company } from '@/hooks/useCompanies';
import { CompanyModuleType } from '@/pages/debtors/_module/useDebtorsActions';
import { PermissionsModel } from '@/hooks/useCompanyPermissionsApi';
import { CompaniesState } from '@/store/modules/companies';
import { EmployeeRole } from '@/hooks/useEmployees';
import { FinanceServiceHandle } from '@/hooks/useFinance';

export type CompanyStat = {
  key: string;
  label: string;
  value: number;
}

export const useDefaultCompany = () => {
  const store = useStore();

  const { t } = useI18n();

  const stats = computed<Array<CompanyStat>>(() => ([
    {
      key: 'decisions',
      value: 0,
    },
    {
      key: 'inCourt',
      value: 0,
    },
    {
      key: 'inProgress',
      value: 0,
    },
    {
      key: 'new',
      value: 0,
    },
    {
      key: 'rejected',
      value: 0,
    },
  ].map((partialStat) => ({
    ...partialStat,
    label: t(`header.stat.${partialStat.key}`),
  }))));

  const company = computed<Company | null>({
    get: () => (
      store.getters['companies/defaultCompany']
    ),
    set: async (id) => {
      await store.dispatch('companies/setDefaultCompanyId', id);
    },
  });

  const companyId = computed(() => company.value?.id);

  const permissionsMap = computed<PermissionsModel>(
    () => store.getters['companies/permissions'],
  );

  const employeeRoles = computed<CompaniesState['employeeRoles']>(
    () => store.getters['companies/employeeRoles'],
  );

  const employeeIsManager = computed(
    () => employeeRoles.value.isOwner
      || employeeRoles.value.employeeRole === EmployeeRole.manager,
  );

  // const permissionsMap = computed<PermissionsModel>(() => ({
  //   [ProductionType.pretrial]: null,
  //   [ProductionType.judicial]: 'write',
  //   [ProductionType.executive]: null,
  // }));

  const companyModuleType = computed(() => company.value?.module as CompanyModuleType);

  const availableServices = computed(
    () => store.state.companies.defaultCompanyAvailableServices as FinanceServiceHandle[],
  );

  return {
    stats,
    company,
    companyId,
    companyModuleType,
    permissionsMap,
    employeeRoles,
    employeeIsManager,
    availableServices,
  };
};
